import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { getAuth, clearAuth, isSuperAdmin } from '../lib/auth';
import { AccountSwitcher } from './AccountSwitcher';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { LayoutDashboard, FileText, Download, Settings as SettingsIcon, LogOut, Shield, Landmark, MapPin, Box, Gauge, Building, Building2, ChevronDown, CreditCard, Receipt, BarChart3, BadgeDollarSign, ArrowUpDown, Zap, AlertOctagon, Sparkles, Activity, Heart, Mail as MailIcon, ListChecks, ShieldCheck, MailWarning, FileSignature, ScrollText, Wallet } from 'lucide-react';
import { AiAgentNavLauncher } from './ai_agent/AiAgentNavLauncher';

export const MainLayout = ({ children, wide = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = getAuth();
  
  // Use state to track current account so we can update it when AccountSwitcher refreshes data
  const [currentAccount, setCurrentAccountState] = useState(() => {
    const str = localStorage.getItem('current_account');
    return str ? JSON.parse(str) : null;
  });
  
  // Re-check localStorage periodically for account updates from AccountSwitcher
  useEffect(() => {
    const checkAccount = () => {
      const str = localStorage.getItem('current_account');
      const acc = str ? JSON.parse(str) : null;
      if (JSON.stringify(acc) !== JSON.stringify(currentAccount)) {
        setCurrentAccountState(acc);
      }
    };
    
    // Check on mount and every second for 10 seconds (to catch AccountSwitcher updates)
    checkAccount();
    const interval = setInterval(checkAccount, 1000);
    const timeout = setTimeout(() => clearInterval(interval), 10000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [location.pathname]); // Re-run on navigation
  
  // Check if UBM is enabled for current account
  const isUbmEnabled = currentAccount?.enabled_apps?.ubm === true;
  const isUbmRoute = location.pathname.startsWith('/ubm');
  
  // Check if Payments is enabled for current account
  const isPaymentsEnabled = currentAccount?.enabled_apps?.payments === true;
  const isPaymentsRoute = location.pathname.startsWith('/payments');
  const isBillsRoute = location.pathname.startsWith('/bills');
  
  // IntakeHub: only visible if enabled_apps.intake_hub is explicitly enabled for the org
  const isIntakeHubEnabled = currentAccount?.enabled_apps?.intake_hub === true;

  // Procurement: Contracts module gated by enabled_apps.contracts
  const isContractsEnabled = currentAccount?.enabled_apps?.contracts === true;
  const isBudgetsEnabled = currentAccount?.enabled_apps?.budgets === true;
  const isProcurementRoute = location.pathname.startsWith('/procurement');

  const handleLogout = () => {
    clearAuth();
    window.location.href = '/login';
  };

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
      isActive
        ? 'bg-primary text-primary-foreground'
        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
    }`;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/billportal-logo.png" alt="BillPortal" className="h-8 w-8" />
            <h1 className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
              BillPortal
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {isSuperAdmin() && (
              <Button
                variant="ghost"
                size="sm"
                className="rounded-none"
                onClick={() => navigate('/super-admin')}
                data-testid="super-admin-link"
              >
                <Shield className="h-4 w-4 mr-2" />
                Super Admin
              </Button>
            )}
            <AccountSwitcher />
            <span className="text-sm text-muted-foreground">{user?.name}</span>
            <Button
              data-testid="settings-button"
              variant="ghost"
              size="sm"
              className="rounded-none"
              onClick={() => navigate('/settings')}
            >
              <SettingsIcon className="h-4 w-4" />
            </Button>
            <Button
              data-testid="logout-button"
              variant="ghost"
              size="sm"
              className="rounded-none"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Horizontal Navigation Menu */}
      <nav className="border-b border-border bg-card">
        <div className="px-4 md:px-6">
          <div className="flex items-center justify-between gap-1">
            <div className="flex items-center gap-1">
            <NavLink to="/dashboard" className={navLinkClass} data-testid="nav-dashboard">
              <LayoutDashboard className="h-4 w-4" />
              Dashboard
            </NavLink>
            {isIntakeHubEnabled && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-md ${
                      (location.pathname === '/bill-jobs' || location.pathname.startsWith('/intake-hub'))
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    data-testid="nav-intake-hub-dropdown"
                  >
                    <FileText className="h-4 w-4" />
                    Intake Hub
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate('/bill-jobs')}
                    data-testid="nav-intake-jobs"
                  >
                    <FileText className="h-4 w-4 mr-2" /> Bill Jobs
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate('/intake-hub/validation-report')}
                    data-testid="nav-intake-validation-report"
                  >
                    <ShieldCheck className="h-4 w-4 mr-2" /> Validation Report
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate('/intake-hub/missing-bills')}
                    data-testid="nav-intake-missing-bills"
                  >
                    <MailWarning className="h-4 w-4 mr-2" /> Missing Bills
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {/* Bulk Downloads hidden — uncomment when needed
            <NavLink to="/bulk-downloads" className={navLinkClass} data-testid="nav-bulk-downloads">
              <Download className="h-4 w-4" />
              Bulk Downloads
            </NavLink>
            */}
            
            {/* Bills - Dropdown with Bills List + Dashboard (requires payments enabled) */}
            {isPaymentsEnabled && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isBillsRoute
                        ? 'bg-primary text-primary-foreground' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    } rounded-md`}
                    data-testid="nav-bills-dropdown"
                  >
                    <Receipt className="h-4 w-4" />
                    Bills
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-44">
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate('/bills/dashboard')}
                    data-testid="nav-bills-dashboard"
                  >
                    <BarChart3 className="h-4 w-4 mr-2" /> Bills Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate('/bills')}
                    data-testid="nav-bills-list"
                  >
                    <Receipt className="h-4 w-4 mr-2" /> All Bills
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            {/* Payments Dropdown - Only show if enabled for org */}
            {isPaymentsEnabled && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isPaymentsRoute
                        ? 'bg-primary text-primary-foreground' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    data-testid="nav-payments"
                  >
                    <CreditCard className="h-4 w-4" />
                    Payments
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48 rounded-none">
                  <DropdownMenuItem 
                    onClick={() => navigate('/payments/dashboard')}
                    className="cursor-pointer"
                    data-testid="nav-payments-dashboard"
                  >
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/payments')}
                    className="cursor-pointer"
                    data-testid="nav-payments-files"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Payment Files
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/payments/bills')}
                    className="cursor-pointer"
                    data-testid="nav-payments-bills"
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    PF Bills
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/payments/service-fees')}
                    className="cursor-pointer"
                    data-testid="nav-payments-service-fees"
                  >
                    <BadgeDollarSign className="h-4 w-4 mr-2" />
                    Service Fee Report
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/payments/variance')}
                    className="cursor-pointer"
                    data-testid="nav-payments-variance"
                  >
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    Variance Report
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/payments/credit-debit')}
                    className="cursor-pointer"
                    data-testid="nav-payments-credit-debit"
                  >
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    Credit / Debit Report
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/payments/refunds-report')}
                    className="cursor-pointer"
                    data-testid="nav-payments-refunds-report"
                  >
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    Refunds Report
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/payments/late-fee-report')}
                    className="cursor-pointer"
                    data-testid="nav-payments-late-fee-report"
                  >
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    Late Fee Report
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/payments/emergency-bill-upload')}
                    className="cursor-pointer"
                    data-testid="nav-payments-emergency-bill-upload"
                  >
                    <AlertOctagon className="h-4 w-4 mr-2" />
                    Emergency Paid Bill Upload
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Procurement Dropdown - Contracts & Budgets (gated by enabled_apps.contracts/budgets) */}
            {(isContractsEnabled || isBudgetsEnabled) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isProcurementRoute
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    data-testid="nav-procurement"
                  >
                    <ScrollText className="h-4 w-4" />
                    Procurement
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  {isContractsEnabled && (
                    <DropdownMenuItem onClick={() => navigate('/procurement/dashboard')} className="cursor-pointer" data-testid="nav-procurement-dashboard">
                      <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                    </DropdownMenuItem>
                  )}
                  {isContractsEnabled && (
                    <DropdownMenuItem onClick={() => navigate('/procurement/contracts')} className="cursor-pointer" data-testid="nav-procurement-contracts">
                      <FileSignature className="h-4 w-4 mr-2" /> Contracts
                    </DropdownMenuItem>
                  )}
                  {isBudgetsEnabled && (
                    <DropdownMenuItem onClick={() => navigate('/procurement/budgets')} className="cursor-pointer" data-testid="nav-procurement-budgets">
                      <Wallet className="h-4 w-4 mr-2" /> Budgets
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* UBM Dropdown - Next to Payments */}
            {isUbmEnabled && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isUbmRoute 
                        ? 'bg-primary text-primary-foreground' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    data-testid="nav-ubm"
                  >
                    <Landmark className="h-4 w-4" />
                    UBM Onboarding
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48 rounded-none">
                  <DropdownMenuItem onClick={() => navigate('/ubm/properties')} className="cursor-pointer">
                    <MapPin className="h-4 w-4 mr-2" /> Properties
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/ubm/units')} className="cursor-pointer">
                    <Box className="h-4 w-4 mr-2" /> Units
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/ubm/meters')} className="cursor-pointer">
                    <Gauge className="h-4 w-4 mr-2" /> Meters
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/ubm/vendors')} className="cursor-pointer">
                    <Building className="h-4 w-4 mr-2" /> Vendor Accounts
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/ubm/bill2rollin')} className="cursor-pointer" data-testid="nav-ubm-bill2rollin">
                    <Zap className="h-4 w-4 mr-2" /> AI Bill2Rollin
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            </div>
            <AiAgentNavLauncher />
          </div>
        </div>
      </nav>

      {/* Page Content */}
      <main className="px-4 md:px-6 py-6">
        {children}
      </main>
    </div>
  );
};

export default MainLayout;
